import { useState, useEffect, useRef } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { useAuth } from '../../../../utils/Auth';
import { googleMapsLoader } from '../../../../utils/QRFunctions/googleMapsLoader';
import styles from './GoogleReview.module.css';

const GoogleReview = () => {
    const { dataDispatch } = useAuth();
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const autocompleteRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const initializeAutocomplete = async () => {
            try {
                // Use shared Google Maps loader
                await googleMapsLoader.load();
                
                if (!inputRef.current) return;

                // Double-check that places library is available
                if (!window.google?.maps?.places?.Autocomplete) {
                    throw new Error('Google Maps Places library not available');
                }

                // Use traditional Autocomplete API (more React-friendly)
                const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
                    types: ['establishment'],
                    fields: ['place_id', 'name', 'formatted_address', 'geometry', 'rating', 'user_ratings_total']
                });

                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    
                    if (place.place_id) {
                        const placeData = {
                            id: place.place_id,
                            displayName: place.name,
                            formattedAddress: place.formatted_address,
                            rating: place.rating,
                            userRatingCount: place.user_ratings_total,
                            location: place.geometry?.location
                        };
                        
                        setSelectedPlace(placeData);
                        setInputValue(place.name);
                        
                        // Build Google Reviews URL
                        const reviewsUrl = `https://search.google.com/local/writereview?placeid=${place.place_id}`;
                        
                        // Store in redirect_url so it works with existing infrastructure
                        dataDispatch({ type: 'redirect', data: reviewsUrl });
                    }
                });

                autocompleteRef.current = autocomplete;
                setIsLoaded(true);
                
            } catch (error) {
                console.error('Error initializing autocomplete:', error);
                setIsLoaded(false); // Reset loading state so user can retry
            }
        };

        initializeAutocomplete();

        return () => {
            // Let React handle cleanup naturally
            autocompleteRef.current = null;
        };
    }, [dataDispatch]);

    const renderBusinessPreview = () => {
        if (!selectedPlace) return null;

        return (
            <div className={styles.businessPreview}>
                <div className={styles.businessHeader}>
                    <div className={styles.businessInfo}>
                        <h4 className={styles.businessName}>{selectedPlace.displayName}</h4>
                        <p className={styles.businessAddress}>{selectedPlace.formattedAddress}</p>
                        {selectedPlace.rating && (
                            <div className={styles.rating}>
                                <span className={styles.stars}>
                                    {'★'.repeat(Math.floor(selectedPlace.rating))}
                                    {selectedPlace.rating % 1 >= 0.5 && '☆'}
                                </span>
                                <span className={styles.ratingText}>
                                    {selectedPlace.rating} ({selectedPlace.userRatingCount || 0} reviews)
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.reviewPrompt}>
                    <div className={styles.reviewIcon}>
                        <i className="fa-brands fa-google"></i>
                    </div>
                    <div className={styles.reviewText}>
                        <h5>Leave a Review</h5>
                        <p>Help others by sharing your experience at {selectedPlace.displayName}</p>
                    </div>
                </div>
            </div>
        );
    };



    return (
        <div className={styles.container}>
            <OverlayTrigger 
                placement='top'
                delay={{show: 250, hide: 400}}
                trigger={["click", "hover"]}
                overlay={<Tooltip><p>Search for your business to create a direct link to your Google Reviews page. Customers can easily leave reviews!</p></Tooltip>}
            >
                <h5 className='mb-2'>Google Reviews QR <i className="fa-regular fa-circle-info"></i></h5>
            </OverlayTrigger>
            <div className={styles.inputGroup}>
                <label className={styles.label}>Search for your business</label>
                <div className={styles.autocompleteContainer}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder="Enter your business name or address"
                        className={styles.input}
                        disabled={!isLoaded}
                    />
                    {!isLoaded && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.loadingText}>Loading Google Places...</div>
                        </div>
                    )}
                </div>
            </div>

            {selectedPlace && (
                <div className={styles.previewContainer}>
                    {renderBusinessPreview()}
                </div>
            )}
        </div>
    );
};

export default GoogleReview; 